import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import snakecaseKeys from 'snakecase-keys';
import camelCaseRecursive from 'camelcase-keys-recursive';
import { sendAlert } from '../../../actions/utils';
import { ComponentDataTable, DefaultModal, SimpleCenteredModal } from '../../../components';
import {
  approveOvertimeCompensationRequest,
  deleteOvertimeCompensationRequest,
  fetchOvertimeCompensationRequests,
  rejectOvertimeCompensationRequest
} from '../../../requests/overtimeCompensations';
import OvertimeCompensationShow from './OvertimeCompensationShow';
import SignatureKeyForm from '../../DocumentPreview/SignatureKeyForm';
import { signWorkflowRequestRequest } from '../../../requests/workflowRequests';
import WorkflowRequestReject from '../../WorkflowRequest/WorkflowRequestReject';

const OvertimeCompensationDatatable = ({
  rrhh = false,
  columns,
  preName,
  moreData,
  setMoreData,
  defaultStartDate = null,
  defaultEndDate = null,
  withMassActions,
  currentEmployee,
  customParams = {},
  nameRangePicker = 'date_range'
}) => {
  const [amount, setAmount] = useState(0);
  const [centerModalShow, setCenterModalShow] = useState(false);
  const [modalBody, setModalBody] = useState('');
  const [modalItem, setModalItem] = useState({});
  const [modalShow, setModalShow] = useState(false);
  const [modalAction, setModalAction] = useState(() => null);
  const [modalTitle, setModalTitle] = useState('');
  const [onRequest, setOnRequest] = useState(true);
  const [compensationRequests, setCompensationRequests] = useState([]);

  const dispatch = useDispatch();

  const handleRequest = params => {
    setOnRequest(true);
    const sendParams = {
      ...params,
      ...customParams,
      filter_employee: rrhh ? '' : currentEmployee
    };
    fetchOvertimeCompensationRequests({
      dispatch,
      params: sendParams,
      successCallback: response => {
        const { data, metadata } = response.data;
        setCompensationRequests(data);
        setAmount(metadata.amount);
        setOnRequest(false);
      },
      failureCallback: () => {
        setOnRequest(false);
      }
    });
  };

  const handleModalClose = () => {
    setCenterModalShow(false);
    setModalShow(false);
    setModalBody('');
    setModalTitle('');
    setOnRequest(false);
  };

  const handleSuccessRemove = () => {
    dispatch(sendAlert({ kind: 'success', message: 'Solicitud eliminada con éxito' }));
    setMoreData(!moreData);
  };

  const removeCompensationRequest = item => {
    setOnRequest(true);
    deleteOvertimeCompensationRequest(item.id, {
      dispatch,
      successCallback: handleSuccessRemove,
      callback: () => handleModalClose()
    });
  };

  const signDocument = (formValues, { workflowRequestId }, setSubmitting = () => null) => {
    signWorkflowRequestRequest(workflowRequestId, {
      dispatch,
      params: snakecaseKeys(formValues),
      successCallback: () => {
        sendAlert({ kind: 'success', message: 'Solicitud firmada y aprobada con éxito' });
        setMoreData(!moreData);
        handleModalClose();
      },
      callback: () => setSubmitting(false)
    });
  };

  const handleApproveRequest = item => {
    const requestId = item.id;
    approveOvertimeCompensationRequest(requestId, {
      dispatch,
      params: { id: requestId },
      successCallback: () => {
        sendAlert({ kind: 'success', message: 'Solicitud aprobada con éxito' });
        setMoreData(!moreData);
        handleModalClose();
      },
      failureCallback: error => {
        sendAlert({ kind: 'error', message: 'Error al aprobar la solicitud' });
        console.error(error);
      }
    });
  };

  const handleRejectRequest = (item, setSubmitting = () => null) => {
    const requestId = item.workflowRequest.record.id;
    const sendParams = snakecaseKeys(item);

    setOnRequest(true);
    rejectOvertimeCompensationRequest(requestId, {
      dispatch,
      params: sendParams,
      formData: true,
      successCallback: () => {
        sendAlert({ kind: 'success', message: 'Solicitud rechazada con éxito' });
        setMoreData(!moreData);
        handleModalClose();
      },
      callback: () => {
        setOnRequest(false);
        setSubmitting(false);
      }
    });
  };

  const handleApproveModal = item => {
    setModalTitle(`${item.requestReviewerSignature ? 'Firmar y ' : ''}Aprobar Solicitud de Horas Extras`);

    if (item.requestReviewerSignature) {
      setModalBody(
        <SignatureKeyForm item={item} onHide={handleModalClose} formRequest={signDocument} signatureKey="" />
      );
      setModalAction(() => null);
    } else {
      setModalItem(item);
      setModalBody(<p>¿Estás seguro que deseas aprobar esta solicitud?</p>);
      setModalAction(() => handleApproveRequest);
    }

    setModalShow(true);
  };

  const handleActions = (item, action) => {
    switch (action) {
      case 'show':
        setModalTitle('Detalle de la Solicitud');
        setModalBody(<OvertimeCompensationShow item={item} />);
        setCenterModalShow(true);
        break;
      case 'approve':
        handleApproveModal(camelCaseRecursive(item));
        break;
      case 'reject':
        setModalTitle('Rechazar Solicitud');
        setModalBody(
          <WorkflowRequestReject
            record={item}
            workflowRequest={item}
            cancelAction={handleModalClose}
            formRequest={handleRejectRequest}
          />
        );
        setCenterModalShow(true);
        break;
      case 'destroy':
        setModalItem(item);
        setModalTitle('Eliminar Solicitud');
        setModalBody('¿Estás seguro que deseas eliminar esta solicitud?');
        setModalShow(true);
        setModalAction(() => removeCompensationRequest);
        break;
      default:
        console.log('Acción no encontrada:', action);
        break;
    }
  };

  const sortColumnCase = name => {
    switch (name) {
      case 'created_at':
        return { sort_column: 'created_at' };
      case 'start_date':
        return { sort_column: 'start_date' };
      case 'end_date':
        return { sort_column: 'end_date' };
      case 'reincorporation_date':
        return { sort_column: 'reincorporation_date' };
      case 'days_taken':
        return { sort_column: 'days_taken' };
      case 'extra_hours_taken':
        return { sort_column: 'extra_hours_taken' };
      case 'status':
        return { sort_status: 'status' };
      default:
        return { sort_column: name };
    }
  };

  return (
    <>
      <div className="data-table-yellow">
        <ComponentDataTable
          columns={columns(handleActions)}
          defaultEndDate={defaultEndDate}
          defaultStartDate={defaultStartDate}
          data={compensationRequests}
          handleSortCase={sortColumnCase}
          moreData={moreData}
          onRequest={onRequest}
          onRowClicked={item => handleActions(item, 'show')}
          pointerOnHover
          preName={preName || 'compensation'}
          rangePicker
          nameRangePicker={nameRangePicker}
          resourceRequest={handleRequest}
          selectableRows={withMassActions}
          totalRows={amount}
          withStartDate
          withEndDate
          withMassActions={withMassActions}
        />
      </div>
      <SimpleCenteredModal
        title={modalTitle}
        size="md"
        body={modalBody}
        show={centerModalShow}
        onHide={handleModalClose}
      />
      <DefaultModal
        title={modalTitle}
        body={modalBody}
        show={modalShow}
        handleClose={handleModalClose}
        handleConfirm={() => modalAction(modalItem)}
        disabled={onRequest}
        titleBtnClose="Cancelar"
        titleBtnSave="Confirmar"
        withClose={modalAction !== null}
        withConfirm={modalAction !== null}
      />
    </>
  );
};

export default OvertimeCompensationDatatable;
